//Global imports
import { useEffect, useState } from 'react';
import {
    CTable,
    CTableBody,
    CTableHead,
    CTableHeaderCell,
    CTableRow,
    CTableDataCell,
    CPagination,
    CPaginationItem,
} from '@coreui/react';
import useInitialLoad from '../../services/Hooks/useInitialLoad';
import CIcon from '@coreui/icons-react';
import { cilFolder } from '@coreui/icons';

//File imports
import { KeywordService } from './Api/KeywordService';
import Notification from '../../services/Notification/Notification';
import { Language } from '../../language/English';
import Logger from '../../services/Logger/Logger';
import { KeywordPojo } from './Api/KeywordPojo';

//Component imports
import { Layout } from '../../components/Layout';
import Loader from '../../components/Layout/Loader';

const ITEMS_PER_PAGE = 12;

const Keywords = () => {
    const keywordService = new KeywordService();
    const notification = new Notification();
    const logger = new Logger();
    const [currentPage, setCurrentPage] = useState(1);
    const [keywordData, setKeywordData] = useState<KeywordPojo[]>([]);
    const [showLoader, setShowLoader] = useState<boolean>(false);
    const [totalPages, setTotalPages] = useState<number>(0);
    const [allKeywords, setAllKeywords] = useState<KeywordPojo[] | null>(null);

    useInitialLoad({
        loaderCallback: () => {
            setShowLoader(true);
            fetchAllKeywords();
        },
    });

    const fetchAllKeywords = async () => {
        try {
            const response = await keywordService.getAllKeywords();
            if (response && response.isSuccess) {
                setAllKeywords(response.keywords);
                if (response && response.keywords.length > 0) {
                    setTotalPages(
                        Math.ceil(response.keywords.length / ITEMS_PER_PAGE),
                    );
                }
            } else {
                notification.error(Language.CANNOT_LOAD_KEYWORDS_ERROR);
            }
            setShowLoader(false);
        } catch (e) {
            notification.error(Language.SOMETHING_IS_WRONG);
            setShowLoader(false);
            logger.error(e);
        }
    };

    useEffect(() => {
        if (allKeywords && allKeywords.length > 0) {
            const start = (currentPage - 1) * ITEMS_PER_PAGE;
            const end = start + ITEMS_PER_PAGE;
            setKeywordData(allKeywords.slice(start, end));
        }
    }, [currentPage, allKeywords]);

    return (
        <Layout>
            {showLoader ? (
                <Loader />
            ) : allKeywords ? (
                allKeywords.length > 0 ? (
                    <>
                        <CTable hover bordered>
                            <CTableHead>
                                <CTableRow>
                                    <CTableHeaderCell style={{ width: '85%' }}>
                                        {Language.KEYWORD}
                                    </CTableHeaderCell>
                                    <CTableHeaderCell
                                        style={{ width: '15%' }}
                                        className="text-center"
                                    >
                                        {Language.COUNT}
                                    </CTableHeaderCell>
                                </CTableRow>
                            </CTableHead>
                            <CTableBody>
                                {keywordData.map((item, index) => (
                                    <CTableRow key={index}>
                                        <CTableDataCell
                                            style={{ width: '85%' }}
                                        >
                                            {item.searchKeyword}
                                        </CTableDataCell>
                                        <CTableDataCell
                                            style={{ width: '15%' }}
                                            className="text-center"
                                        >
                                            {item.totalCount}
                                        </CTableDataCell>
                                    </CTableRow>
                                ))}
                            </CTableBody>
                        </CTable>

                        <div className="d-flex justify-content-end mt-3">
                            <CPagination>
                                <CPaginationItem
                                    disabled={currentPage === 1}
                                    onClick={() =>
                                        setCurrentPage((prev) => prev - 1)
                                    }
                                >
                                    &laquo;
                                </CPaginationItem>
                                {Array.from({ length: totalPages }, (_, i) => (
                                    <CPaginationItem
                                        key={i}
                                        active={i + 1 === currentPage}
                                        onClick={() => setCurrentPage(i + 1)}
                                    >
                                        {i + 1}
                                    </CPaginationItem>
                                ))}
                                <CPaginationItem
                                    disabled={currentPage === totalPages}
                                    onClick={() =>
                                        setCurrentPage((prev) => prev + 1)
                                    }
                                >
                                    &raquo;
                                </CPaginationItem>
                            </CPagination>
                        </div>
                    </>
                ) : (
                    <div
                        className="d-flex flex-column align-items-center justify-content-center"
                        style={{ minHeight: '60vh' }}
                    >
                        <CIcon
                            icon={cilFolder}
                            size="3xl"
                            className="mb-3 text-secondary"
                        />
                        <h4 className="mb-3 text-center text-secondary fs-6">
                            {Language.NO_KEYWORDS_FOUND} <br />
                        </h4>
                    </div>
                )
            ) : null}
        </Layout>
    );
};

export default Keywords;
